import React from 'react'
import { isEmpty } from 'lodash-es'
import { getQueryParam } from './query'
import { getProductDetailByPropertyName, PRODUCT_NAME_VARIATION_TYPES } from 'universal/cfs-card'
import { isFlowSupported } from 'universal/ucs'
import { INTENT_TYPES } from 'universal/constants'
import {
  getToastMessagesFromSessionStorage,
  removeToastMessagesFromSessionStorage,
} from 'utils/storage'

const DEFAULT_PAGE_SLIDE_DELAY = 350

export function firstDefined() {
  for (let arg of arguments) {
    if (typeof arg !== 'undefined') {
      return arg
    }
  }
  return null
}

export function removeClassName(elementId, className) {
  const element = document.getElementById(elementId)
  if (element) {
    const elClassName = element.className || ''
    element.className = elClassName.replace(new RegExp(`\s?${className}\?`), '')
  }
}

export function appendClassName(elementId, className) {
  const element = document.getElementById(elementId)
  if (element) {
    const elClassName = element.className || ''
    if (!elClassName.includes(className)) {
      element.className = `${element.className} ${className}`
    }
  }
}

export const formatNewLines = (string) => {
  return string.split('\n').map((item, index) => {
    return <div key={index}>{item}</div>
  })
}

export const afterPageSlide = (callback, delay = DEFAULT_PAGE_SLIDE_DELAY) => {
  setTimeout(callback, delay)
}

/**
 * To delay the display of the next page to improve user experience, such as on PIN input complete
 * so that user can observe that the PIN has been entered.
 */
export const delayNextStep = (callback, delay = 200) => {
  setTimeout(callback, delay)
}

/**
 * Checks if a PIN contains consecutive numbers in ascending or descending order.
 * Used for US PPDC flow.
 * 
 * BLOCKS: Consecutive sequences like 1234, 2345, 4321, 5432, etc.
 * ALLOWS: Non-consecutive sequences like 0246, 1357, 2468, 3579, etc.
 * 
 * @param {string} pinString - The PIN string to validate (4 digits)
 * @returns {boolean} - Returns true if PIN contains consecutive numbers (invalid), false otherwise
 */
export const isPinFoundSequenced = (pinString = '') => {
  const FORWARD_SEQUENCE_PIN_LIST = /^(0123|1234|2345|3456|4567|5678|6789)$/
  const BACKWARD_SEQUENCE_PIN_LIST = /^(3210|4321|5432|6543|7654|8765|9876)$/

  return FORWARD_SEQUENCE_PIN_LIST.test(pinString) || BACKWARD_SEQUENCE_PIN_LIST.test(pinString)
}

// Alias for better naming
export const isPinConsecutive = isPinFoundSequenced

/**
 * Checks if a PIN contains alternating numbers (skip-by-2 only).
 * Used for UK PPDC flow to show specific error message for alternating patterns.
 * 
 * BLOCKS: Skip-by-2 patterns like:
 *   - 0246, 1357, 2468, 3579 (ascending)
 *   - 6420, 7531, 8642, 9753 (descending)
 * ALLOWS: Skip-by-3 (0369, 9630) and consecutive (checked separately) and random patterns
 * 
 * @param {string} pinString - The PIN string to validate (4 digits)
 * @returns {boolean} - Returns true if PIN contains alternating pattern (invalid), false otherwise
 */
export const isPinAlternating = (pinString = '') => {
  // Block skip-by-2 sequences (±2) ONLY - do NOT block skip-by-3
  const SKIP_2_FORWARD = /^(0246|1357|2468|3579)$/
  const SKIP_2_BACKWARD = /^(6420|7531|8642|9753)$/

  return SKIP_2_FORWARD.test(pinString) || SKIP_2_BACKWARD.test(pinString)
}

/**
 * Checks if a PIN contains ANY sequential pattern for UK (consecutive or skip-by-2).
 * Used for UK PPDC flow - stricter than US but allows skip-by-3.
 * 
 * BLOCKS:
 *   - Consecutive (±1): 1234, 2345, 4321, 5432, etc. (14 patterns)
 *   - Skip-by-2 (±2): 0246, 1357, 2468, 3579, 6420, 7531, 8642, 9753 (8 patterns)
 * ALLOWS:
 *   - Skip-by-3 (±3): 0369, 9630 ✅ ALLOWED
 *   - Random PINs: 1362, 5927, etc.
 * 
 * @param {string} pinString - The PIN string to validate (4 digits)
 * @returns {boolean} - Returns true if PIN contains blocked pattern (invalid), false otherwise
 */
export const isPinSequencedUK = (pinString = '') => {
  return isPinConsecutive(pinString) || isPinAlternating(pinString)
}

export const isPinFoundIdentical = (pinString = '') => /^(.)\1*$/.test(pinString)

export const getNetworkStatus = () => {
  return window.navigator.onLine
}

export const getProductQueryParam = (productName) => {
  const productNameQueryParam = getQueryParam('productName')

  return (
    getProductDetailByPropertyName(productNameQueryParam, PRODUCT_NAME_VARIATION_TYPES.ALIAS)
      ?.alias || productName
  )
}

export function isValidProductName(flowName, productName) {
  if (!productName) {
    return false
  }
  return isFlowSupported(flowName, productName)
}

export const isFunction = (callback) => {
  return callback && {}.toString.call(callback) === '[object Function]'
}

export function isValidIntent(intent) {
  return Object.values(INTENT_TYPES).includes(intent)
}

export function getModalTitleContentForEnrollmentFlow(
  intent,
  { id, variable = { cardName: 'PayPal Debit Card' } },
) {
  if (intent === INTENT_TYPES.ENROLL) {
    return { id, variable }
  }
  return {}
}

export function getToastMessageToDisplay(whitelistedEventIds = []) {
  const toastMessagesFromSessionStorage = getToastMessagesFromSessionStorage()
  removeToastMessagesFromSessionStorage()
  const toastMessageEventIds = Object.keys(toastMessagesFromSessionStorage)

  if (!toastMessageEventIds.length) {
    return null
  }

  const toastMessageEventId = toastMessageEventIds[0]

  const isEventIdWhitelisted = whitelistedEventIds.includes(toastMessageEventId)
  if (isEventIdWhitelisted) {
    return toastMessagesFromSessionStorage[toastMessageEventId]
  }

  return null
}

export function handleOnKeyDownToClickOnElement(e) {
  if (e.key === 'Enter') {
    e.currentTarget.click()
  }
}

export function renameKeys(data, keyMap) {
  return Object.entries(data).reduce((newData, [key, value]) => {
    const newKey = keyMap[key] || key
    newData[newKey] = value
    return newData
  }, {})
}

export function updateQueryParameters(obj) {
  const searchString = window.location.search
  const searchParams = new URLSearchParams(searchString)

  for (const key in obj) {
    searchParams.set(key, obj[key])
  }

  const newSearchString = searchParams.toString()

  window.history.pushState({}, '', `?${newSearchString}`)
}

export function isModifierKey(inputKey) {
  return [
    'Backspace',
    'Delete',
    'Tab',
    'Meta',
    'Control',
    'ArrowLeft',
    'ArrowRight',
    'Enter',
  ].includes(inputKey)
}

export function appendUrlQueryParams(urlString = '', queryParams = {}) {
  if (!urlString || isEmpty(queryParams)) {
    return ''
  }

  const url = new URL(urlString)
  Object.entries(queryParams).forEach(([key, value]) => {
    if (key && value) {
      url.searchParams.append(key, value)
    }
  })

  return url.toString()
}


export function isInIframe() {
  return window.self !== window.parent
}